import { PlusOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import { removeEmptyOrNull } from 'library/helpers/utility';
import { useManagePermittedRoles } from 'library/hooks';
import { Fragment, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { sitesActions } from 'reduxStore/sites/slice';
import { SelectAddressAutoComplete, UploadBox } from 'zComponents/atoms';
import { Button, Divider, Flex, Form, Input, InputNumber, Modal, Tooltip, Typography } from 'zui/Antd';

const { Text, Title } = Typography;

function AddSiteModal({ children, open = false, setOpen }) {
	//Hooks
	const [form] = Form.useForm();
	const {
		token: {
			paddingXL,
			colorText,
			colorTextLabel,
			fontSizeSM,
			paddingXXL = 48,
			paddingMD,
			paddingXS,
			paddingSM,
			fontSizeLG,
			fontWeightStrong
		}
	} = theme.useToken();
	const dispatch = useDispatch();
	const uploadRef = useRef(null);

	// Local State
	const [loading, setLoading] = useState(false);
	const [imagePath, setImagePath] = useState(null);
	const [image, setImage] = useState(null);
	const [internalVisible, setInternalVisible] = useState(false);

	// Determine visibility state
	const visible = open || internalVisible;
	const setVisible = setOpen || setInternalVisible;

	// Determine who has permission to Add Site
	const isPermittedCreateSite = useManagePermittedRoles('addSite'); //

	const handleCancel = () => {
		form.resetFields();
		setVisible(false);
		setImagePath(null);
		setImage(null);
	};

	const handleFinish = (values) => {
		setLoading(true);

		const payload = {
			name: values.name
		};

		// Handle Map fields (optional)
		if (values.xDimension && values.yDimension && imagePath) {
			payload.dimensionX = Number(values.xDimension);
			payload.dimensionY = Number(values.yDimension);
			payload.url = imagePath;
		}

		// Handle Address fields (optional)
		if (values.address) {
			payload.addressLine1 = values.addressLine1;
			payload.addressLine2 = values.addressLine2 || null;
			payload.suburb = values.suburb;
			payload.state = values.state;
			payload.country = values.country;
			payload.postCode = values.postCode;
			payload.latitude = values.latitude || null;
			payload.longitude = values.longitude || null;
			payload.fullAddress = values.address;
		}

		dispatch(sitesActions.createSite({ data: removeEmptyOrNull(payload) }));

		setLoading(false);
		setTimeout(() => {
			handleCancel();
		}, 500);
	};

	return (
		<Fragment>
			<Tooltip title={isPermittedCreateSite ? 'Add Site' : "You don't have permission to add a site"}>
				{children ? (
					children
				) : (
					<Button
						type='primary'
						icon={<PlusOutlined />}
						onClick={() => setVisible(true)}
						disabled={!isPermittedCreateSite}
					>
						Add Site
					</Button>
				)}
			</Tooltip>

			<Modal
				open={visible}
				onCancel={handleCancel}
				closeIcon
				centered
				destroyOnClose
				width={1434}
				footer={[
					<Flex gap={paddingSM} justify='flex-end'>
						<Button onClick={handleCancel} type='default'>
							Cancel
						</Button>

						<Button type='primary' htmlType='submit' form='siteForm' loading={loading}>
							Add Site
						</Button>
					</Flex>
				]}
				styles={{ content: { padding: paddingXL }, footer: { marginTop: paddingXXL } }}
			>
				<Flex vertical style={{ marginBottom: paddingXL }}>
					<Title level={4} style={{ margin: 0, color: colorText, lineHeight: '28px' }}>
						Add New Site
					</Title>
					<Text style={{ color: colorTextLabel, fontSize: fontSizeSM }}>
						Add a new site to your organisation. You can add floors to it later.
					</Text>
				</Flex>

				<Form
					id='siteForm'
					form={form}
					layout='vertical'
					onFinish={handleFinish}
					requiredMark={false}
					name='siteForm'
					// style={{ backgroundColor: 'purple' }}
				>
					<Flex gap={paddingXXL}>
						<Flex vertical gap={paddingMD} style={{ width: '100%' }}>
							<Form.Item
								name='name'
								label={<Text style={{ fontWeight: fontWeightStrong }}>Site Name</Text>}
								style={{ marginBottom: 0 }}
								rules={[{ required: true, message: 'Please enter a site name' }]}
							>
								<Input placeholder='eg. Prospect site 1' />
							</Form.Item>

							<Form.Item
								name='address'
								label={'Address'}
								style={{
									fontWeight: fontWeightStrong,
									marginBottom: 0
								}}
							>
								<SelectAddressAutoComplete
									onChange={(parsedAddress) => {
										form.setFieldsValue({
											addressLine1: parsedAddress.addressLine1,
											addressLine2: parsedAddress.addressLine2 || null,
											suburb: parsedAddress.suburb,
											state: parsedAddress.state,
											country: parsedAddress.country,
											postCode: parsedAddress.postCode,
											latitude: parsedAddress.latitude,
											longitude: parsedAddress.longitude,
											address: parsedAddress.formattedAddress
										});
									}}
								/>
							</Form.Item>
							{/* Hidden Address Fields */}
							{[
								'addressLine1',
								'addressLine2',
								'suburb',
								'state',
								'country',
								'postCode',
								'latitude',
								'longitude'
							].map((field) => (
								<Form.Item key={field} name={field} hidden>
									<Input />
								</Form.Item>
							))}
						</Flex>

						<Divider type='vertical' style={{ margin: `0px ${paddingXS}px`, height: 'auto' }} />

						<Flex gap={paddingXL} vertical style={{ width: '100%' }}>
							<Text style={{ fontSize: fontSizeLG, fontWeight: fontWeightStrong }}>
								Site Layout (Optional)
							</Text>
							<Flex gap={paddingSM}>
								<Form.Item
									name='xDimension'
									label={
										<Text style={{ fontWeight: fontWeightStrong, color: colorTextLabel }}>
											X Dimension
										</Text>
									}
									style={{ width: '100%', marginBottom: 0 }}
									dependencies={['yDimension', 'url']}
									rules={[
										({ getFieldValue }) => ({
											validator(_, value) {
												const y = getFieldValue('yDimension');
												if ((y || imagePath) && !value) {
													return Promise.reject(new Error('X Dimension is required'));
												}
												return Promise.resolve();
											}
										})
									]}
								>
									<InputNumber placeholder='eg. 20' style={{ width: '100%' }} min={0} step={1} />
								</Form.Item>

								<Form.Item
									name='yDimension'
									label={
										<Text style={{ fontWeight: fontWeightStrong, color: colorTextLabel }}>
											Y Dimension
										</Text>
									}
									style={{ width: '100%', marginBottom: 0 }}
									dependencies={['xDimension', 'url']}
									rules={[
										({ getFieldValue }) => ({
											validator(_, value) {
												const x = getFieldValue('xDimension');
												if ((x || imagePath) && !value) {
													return Promise.reject(new Error('Y Dimension is required'));
												}
												return Promise.resolve();
											}
										})
									]}
								>
									<InputNumber placeholder='eg. 24.5' style={{ width: '100%' }} min={0} step={1} />
								</Form.Item>
							</Flex>

							<Form.Item
								name='url'
								label={<Text style={{ fontWeight: fontWeightStrong }}>Site Layout</Text>}
								style={{ marginBottom: 0, padding: `0px 2px` }}
								dependencies={['xDimension', 'yDimension']}
								rules={[
									({ getFieldValue }) => ({
										validator(_, value) {
											const x = getFieldValue('xDimension');
											const y = getFieldValue('yDimension');
											if ((x || y) && !imagePath) {
												return Promise.reject(new Error('Site Layout is required'));
											}
											return Promise.resolve();
										}
									})
								]}
							>
								<UploadBox
									entityID={3}
									photoUrl={imagePath || null}
									entityName='Floor'
									shape='square'
									cursor
									edit
									size={330}
									uploadRef={uploadRef}
									setImage={setImage}
									image={image}
									setImagePath={setImagePath}
									imagePath={imagePath}
									placeHolderUpload='Drag and drop or click to upload'
								/>
							</Form.Item>
						</Flex>
					</Flex>
				</Form>
			</Modal>
		</Fragment>
	);
}

export default AddSiteModal;
